import React from 'react'

import PropTypes from 'prop-types'
import classNames from 'classnames'
import TextField from '@material-ui/core/TextField'
import { withStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import { addTestAutomationClassName } from 'utils/misc'

import IconButton from '@material-ui/core/IconButton'
import Visibility from '@material-ui/icons/Visibility'
import InputAdornment from '@material-ui/core/InputAdornment'
import VisibilityOff from '@material-ui/icons/VisibilityOff'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import { FORM_ACTION_ERROR_STATUS } from 'constants/form'

export const styles = theme => ({
  root: {
    textAlign: 'center',
    padding: `0 ${theme.spacing(1)}px`,
  },
  errorMessage: {
    padding: `${theme.spacing(2)}px 0`,
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  formHelperTextLarge: {
    bottom: -25,
  },
  terms: {
    letterSpacing: '0',
    color: theme.palette.text.disabled,
    margin: `${theme.spacing(2)}px 0px`,
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
})

const CreateAccountForm = ({
  values,
  errors,
  classes,
  status,
  showSignin,
  errorMessage,
  isSubmitting,
  showPassword,
  hasFieldError,
  handleFormSubmit,
  handleFieldChange,
  showConfirmPassword,
  togglePasswordVisibility,
  className: classNameProp,
  toggleConfirmPasswordVisibility,
}) => {
  const className = classNames(classes.root, classNameProp)
  return (
    <div className={className}>
      {status === FORM_ACTION_ERROR_STATUS && errorMessage ? (
        <Typography
          className={classes.errorMessage}
          color="error"
          align="center">
          {errorMessage}
        </Typography>
      ) : null}
      <form noValidate autoComplete="off" onSubmit={handleFormSubmit}>
        <FormControl
          className={classes.formControl}
          fullWidth
          error={hasFieldError('email')}>
          <TextField
            name="email"
            autoComplete="email"
            label="Email Address"
            value={values.email}
            onChange={handleFieldChange}
            margin="normal"
            error={hasFieldError('email')}
            inputProps={{
              maxLength: 256,
            }}
            className={addTestAutomationClassName('qa-createaccount-field-email')}
          />
          {hasFieldError('email') ? (
            <FormHelperText>{errors.email}</FormHelperText>
          ) : null}
        </FormControl>
        <FormControl
          className={classes.formControl}
          fullWidth
          error={hasFieldError('name')}>
          <TextField
            name="name"
            label="Name"
            autoComplete="name"
            value={values.name}
            onChange={handleFieldChange}
            type="name"
            error={hasFieldError('name')}
            margin="normal"
            inputProps={{
              maxLength: 1024,
            }}
            className={addTestAutomationClassName('qa-createaccount-field-name')}
          />
          {hasFieldError('name') ? (
            <FormHelperText>{errors.name}</FormHelperText>
          ) : null}
        </FormControl>
        <FormControl
          className={classes.formControl}
          fullWidth
          error={hasFieldError('password')}>
          <TextField
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Password"
            autoComplete="password"
            value={values.password}
            onChange={handleFieldChange}
            error={hasFieldError('password')}
            InputProps={{
              maxLength: 25,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle password visibility"
                    onClick={togglePasswordVisibility}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className={addTestAutomationClassName('qa-createaccount-field-password')}
          />
          {hasFieldError('password') ? (
            <FormHelperText className={classes.formHelperTextLarge}>
              {errors.password}
            </FormHelperText>
          ) : null}
        </FormControl>
        <FormControl
          className={classes.formControl}
          fullWidth
          error={hasFieldError('confirmPassword')}>
          <TextField
            margin="normal"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            label="Confirm Password"
            onChange={handleFieldChange}
            value={values.confirmPassword}
            autoComplete="confirm-password"
            error={hasFieldError('confirmPassword')}
            InputProps={{
              maxLength: 25,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle password visibility"
                    onClick={toggleConfirmPasswordVisibility}>
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className={addTestAutomationClassName('qa-createaccount-field-confirmpassword')}
          />
          {hasFieldError('confirmPassword') ? (
            <FormHelperText>{errors.confirmPassword}</FormHelperText>
          ) : null}
        </FormControl>
        <FormControl className={classes.formControl} fullWidth>
          <SubmitButton
            autoFocus
            size="large"
            type="submit"
            color="primary"
            variant="primary"
            disabled={isSubmitting}
            className={classNames(
              classes.submitButton,
              addTestAutomationClassName('qa-createaccount-button-createaccount')
            )}>
            Create account
          </SubmitButton>
        </FormControl>
        <Typography className={classes.terms}>
          By clicking create account, you confirm that you have read and agreed
          to our terms and conditions
        </Typography>
        {showSignin && (
          <FormControl className={classes.formControl} fullWidth>
            <Typography>
              <Link
                className={addTestAutomationClassName('qa-createaccount-link-signin')}
                href="/login">
                Sign in
              </Link>
            </Typography>
          </FormControl>
        )}
      </form>
    </div>
  )
}

CreateAccountForm.propTypes = {
  apiCallError: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  errorMessage: PropTypes.string,
  errors: PropTypes.object,
  showPassword: PropTypes.bool,
  togglePasswordVisibility: PropTypes.func,
  showConfirmPassword: PropTypes.bool,
  toggleConfirmPasswordVisibility: PropTypes.func,
  handleCheckboxChange: PropTypes.func,
  handleFieldChange: PropTypes.func,
  hasFieldError: PropTypes.func,
  handleFormSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  rememberMe: PropTypes.bool,
  onLogin: PropTypes.func,
  status: PropTypes.string,
  values: PropTypes.object,
  showSignin: PropTypes.bool,
}

export default withStyles(styles)(CreateAccountForm)



// WEBPACK FOOTER //
// ./src/components/organisms/CreateAccountForm/CreateAccountForm.js